import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiMenu, FiX, FiHome, FiTarget, FiUsers, FiSettings, FiBook, FiBarChart3, FiLayout, FiCalendar, FiTrendingUp, FiFileText } = FiIcons;

const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const [imageError, setImageError] = useState(false);

  const navItems = [
    { path: '/', label: 'Dashboard', icon: FiHome },
    { path: '/executive-summary', label: 'Exec Summary', icon: FiTarget },
    { path: '/role-breakdown', label: 'Role Map', icon: FiUsers },
    { path: '/architecture', label: 'Blueprint', icon: FiSettings },
    { path: '/modules', label: 'Modules', icon: FiBook },
    { path: '/sop-library', label: 'SOPs', icon: FiLayout },
    { path: '/reporting', label: 'Reporting', icon: FiFileText },
    { path: '/implementation', label: 'Timeline', icon: FiCalendar },
    { path: '/expansion', label: 'Expansion', icon: FiTrendingUp }
  ];

  const handleImageError = () => {
    setImageError(true);
    console.error('Logo image failed to load');
  };

  const handleImageLoad = () => {
    console.log('Logo loaded successfully');
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-sand/90 backdrop-blur-md border-b border-sand-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          <Link to="/" className="flex items-center space-x-3 group">
            {!imageError ? (
              <img 
                src="https://res.cloudinary.com/quest-agency/image/upload/v1740080345/quest-agent-generated-images/mindgarden-logo-1740080345759.jpg" 
                alt="MindGarden Logo" 
                className="h-12 w-auto rounded-xl shadow-sm group-hover:shadow-md transition-all border border-sage-200 object-cover"
                onError={handleImageError}
                onLoad={handleImageLoad}
                style={{ display: 'block' }}
              />
            ) : (
              <div className="w-12 h-12 rounded-2xl bg-sage-200 flex items-center justify-center border-2 border-sage-300">
                <span className="text-sage-800 font-bold text-xl">MG</span>
              </div>
            )}
            <span className="text-xl font-bold text-charcoal tracking-tight">MindGarden</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex space-x-1">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 flex items-center space-x-2 ${
                  location.pathname === item.path
                    ? 'bg-sage-100 text-sage-800 shadow-sm'
                    : 'text-charcoal-500 hover:text-charcoal hover:bg-white/50'
                }`}
              >
                <SafeIcon icon={item.icon} className={`w-4 h-4 ${location.pathname === item.path ? 'text-sage-600' : 'text-charcoal-500'}`} />
                <span className="hidden xl:block">{item.label}</span>
              </Link>
            ))}
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="lg:hidden p-2 rounded-lg text-charcoal hover:bg-sage-50"
          >
            <SafeIcon icon={isOpen ? FiX : FiMenu} className="w-6 h-6" />
          </button>
        </div>

        {/* Mobile Navigation */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="lg:hidden py-4 border-t border-sand-200 bg-sand"
            >
              <div className="grid grid-cols-2 gap-2">
                {navItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setIsOpen(false)}
                    className={`p-3 rounded-lg text-sm font-medium transition-all duration-200 flex items-center space-x-2 ${
                      location.pathname === item.path
                        ? 'bg-sage-100 text-sage-800'
                        : 'text-charcoal hover:bg-white'
                    }`}
                  >
                    <SafeIcon icon={item.icon} className="w-4 h-4" />
                    <span>{item.label}</span>
                  </Link>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </nav>
  );
};

export default Navigation;